/**
 * v77.99.77: Pure helpers and the async-job export driver used by
 * pages/CourtBundle.jsx. Extracted to keep the page file under the
 * 1000-line maintainability budget. All functions and constants are
 * self-contained — they take everything they need via arguments.
 */
import { API, getToken } from "@/lib/api";
import { generateIdempotencyKey } from "@/lib/idempotency";

/**
 * v77.99.29 — Stage label dictionary. Matches the backend's
 * `progress_stage` values written by `_run_court_bundle_job` (and the
 * "ticker" task inside it). Each stage gets a short, calm sentence
 * shown next to the progress bar so the user knows the job is alive.
 */
export const STAGE_LABELS = {
  queued: "Queued — your export will start in a moment.",
  preparing: "Preparing your bundle.",
  gathering_records: "Gathering records (messages, expenses, journal).",
  rendering_pages: "Rendering pages.",
  // v77.99.161 (iter 139.cb-honest-progress) — "attaching_receipts"
  // is now the ticker's terminal stage during the long-running
  // phase (file I/O + PDF rendering + attachment merge). We only
  // transition to "finalizing" AFTER post_court_bundle returns on
  // the backend. Copy is honest about expected duration for large
  // bundles so the user doesn't think the job is frozen.
  attaching_receipts: "Processing supporting documents — this can take several minutes for large bundles.",
  finalizing: "Finalising and signing the PDF.",
  ready: "Ready — downloading now.",
  incomplete_attention_required: "Ready with items that need attention.",
  running: "Building your bundle.",
  failed: "Export failed.",
};

/**
 * v77.99.29 — Map any export failure to a calm, actionable message. The
 * generic `TypeError: Failed to fetch` from a flaky mobile network
 * would otherwise show up verbatim, which is alarming and unhelpful.
 */
export function describeExportError(err, ctx) {
  const raw = (err && (err.message || err.toString())) || "";
  const lower = raw.toLowerCase();
  if (lower.includes("failed to fetch") || lower.includes("networkerror") || lower.includes("load failed") || lower.includes("could not reach the export service")) {
    return {
      headline: "We couldn't complete this Court Bundle.",
      // v77.99.163 (iter 141.cb-retry-always) — Honest copy. The
      // first POST failed before the job even started — most often
      // during a deploy window on the hosting platform. The user's
      // form state is preserved; Retry re-runs identical values.
      detail:
        "Couldn't reach the export service for a moment — your records and selections are safe. Tap Retry to try again.",
    };
  }
  if (lower.includes("timed out") || lower.includes("timeout") || lower.includes("stalled") || lower.includes("appeared stalled") || lower.includes("worker heartbeat") || lower.includes("server was restarted")) {
    // v77.99.157 (iter 135.cb-stall) — Never blame the user's date
    // range. A combined 9-month, 3-child, expenses+maintenance
    // bundle is a legitimate production request; the system must
    // support it. If we couldn't finish, that's on us — surface
    // Retry as the primary path.
    //
    // v77.99.162 (iter 140.cb-orphan-sweep) — The server-side stall
    // error already contains "Your records and selections are safe;
    // you can retry this export." Reusing the raw message avoids
    // the stuttered duplication the user saw ("safe. you can retry
    // this export.. Your records and selections are safe.").
    return {
      headline: "We couldn't complete this Court Bundle.",
      detail: raw && raw.length > 4
        ? raw.replace(/\.\.$/, ".")
        : "Your records and selections are safe. You can retry this export.",
    };
  }
  if (lower.includes("expired before download")) {
    return {
      headline: "Your export expired before download.",
      detail: "Bundles are held for 7 days. Tap Export again to regenerate.",
    };
  }
  return {
    headline: "We couldn't complete this Court Bundle.",
    detail: raw.length > 4
      ? `${raw} Your records and selections are safe — you can retry this export.`
      : "Your records and selections are safe. You can retry this export.",
  };
}

export function formatBytes(n) {
  if (!n || n <= 0) return "0 KB";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export const SECTIONS = [
  { key: "messages",    label: "Messages",                     hint: "All chat history within the date range, with author + flags." },
  { key: "custody",     label: "Custody schedule & overrides", hint: "Active schedules + every override / handover within the range." },
  // Iter 122.cb-cv — Contact Visits is a first-class section. Each
  // visit gets its own clean page with reference, status, verification
  // and any nested third-party observations.
  { key: "contact_visits", label: "Contact Visits",            hint: "Each visit on a clean section: reference, date, type, status, verification, notes, GPS, attachments and any third-party observations." },
  { key: "expenses",    label: "Expenses & settlements",       hint: "Expense rows + settlement receipts. Receipts attached at the end of the bundle." },
  { key: "upcoming_expenses", label: "Upcoming expenses",      hint: "Heads-up records for big-ticket costs that were flagged in advance. Private records belonging to the other parent are excluded." },
  { key: "maintenance", label: "Maintenance payments",         hint: "Maintenance arrangements + every scheduled payment (paid, late, missed, waived) within the range." },
  { key: "journal",     label: "Journal entries (shared)",     hint: "Only entries explicitly marked as shared. Private notes are never exported." },
  { key: "documents",   label: "Documents",                    hint: "Uploaded document metadata (titles, categories, child link). Files themselves stay private." },
  { key: "profiles",    label: "Child profiles",               hint: "Name, DOB, school, allergies, routines, emergency contact." },
  { key: "audit",       label: "Audit log",                    hint: "Every recorded action (create / edit / delete) within the date range. Off by default. Audit logs can add a lot of pages to a bundle, so switch on only if a court or solicitor specifically asks for it." },
];

/**
 * v77.80 — Sections enabled by default. Audit log is intentionally OFF
 * because it can dramatically inflate bundle length (often the single
 * largest section) and most parents only need it when explicitly asked.
 * Iter 122.cb-cv — Contact Visits is on by default; it's a first-class
 * evidence category and parents preparing a bundle almost always need
 * it included.
 */
export const DEFAULT_ON_SECTIONS = new Set([
  "messages", "custody", "contact_visits", "expenses", "upcoming_expenses", "maintenance", "journal", "documents", "profiles",
]);

export const todayISO = () => new Date().toISOString().slice(0, 10);
export const daysAgoISO = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

const POLL_INTERVAL_MS = 1500;
// v77.99.157 (iter 135.cb-stall) — The frontend no longer imposes a
// wall-clock cap on how long a Court Bundle may take. A legitimate
// combined 9-month, 3-child, expenses+maintenance export can run
// well past 5 minutes on real production households; killing the
// polling loop just because the browser has been waiting for a while
// showed the user a misleading "timed out" toast while the
// background job was still building fine. The server now owns stall
// detection: `GET /exports/jobs/{id}` refuses to leave a job in
// running state once its heartbeat is > STALL_THRESHOLD_SECONDS
// (10 min) old, at which point it flips to `failed` with a clear
// message. This helper simply keeps polling until the server hands
// back a terminal status (`ready` / `incomplete_attention_required`
// / `failed`) or the job 404s (purged).
//
// A defensive absolute ceiling is retained purely so a runaway
// browser tab with a dead server connection can't leak the promise
// forever — 6 hours is well past any realistic bundle build (real
// data ~4 min P95, ~15 min for very large households).
const POLL_ABSOLUTE_CEILING_MS = 6 * 60 * 60 * 1000;
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * v77.85 — Async-job export driver.
 *
 * The legacy synchronous endpoint (`POST /api/exports/court-bundle`)
 * keeps the connection open while ReportLab builds the PDF. On
 * production behind a strict K8s ingress / Cloudflare proxy, the proxy
 * treats "no body bytes for N seconds" as an idle connection and drops
 * it — the browser surfaces this as the generic `TypeError: Failed to
 * fetch` toast. The new flow:
 *   1. POST  /exports/court-bundle/start  -> { job_id }  (~150ms)
 *   2. GET   /exports/jobs/{id}           -> poll status
 *   3. GET   /exports/jobs/{id}/download  -> single fast burst when ready
 *
 * Returns `{ blob, fingerprint, filename }` and triggers the browser
 * save dialog directly. Throws a `toast`-ready Error on failure.
 */
export async function runAsyncCourtBundleJob({
  start_date, end_date, child_ids, sections, label, onProgress,
  // v77.99.98 — Optional async batch notification. When set, the
  // backend groups sibling jobs by `batch_id` and fires ONE combined
  // email to the user once every sibling has reached a terminal state
  // (ready/failed). The helper itself still polls + downloads as
  // before; the email is purely additive — it just means the user
  // doesn't have to keep the tab open to know when the run is done.
  batch_id,
  batch_expected_count,
  notify_email_on_ready,
  // Iter 122.cb-cv — Per-section Contact Visits options (mode, filters,
  // per-visit content toggles). Ignored when `contact_visits` is not
  // in `sections`.
  contact_visits_options,
  // iter 126.cb-relationship — When set, the backend narrows the
  // bundle to a single parent relationship. Standard child scope
  // intersection still applies.
  relationship_id,
}) {
  // v77.99.102 — Cookie-only auth. The legacy localStorage token
  // is gone; getToken() returns "" so the Bearer header is empty
  // and the HttpOnly cookie carries auth. Mediator/Bearer callers
  // can still supply a token via the optional `bearer_token` arg
  // (used by the testing infrastructure that has no cookie jar).
  const token = getToken();
  // No longer block when token is empty — the cookie covers SPA users.
  // 1. Kick off the job — short, responds in milliseconds.
  // v77.99.18 — Idempotency-Key on court-bundle export start. A
  // double-click on the "Export" button or a flaky network retry
  // mustn't enqueue two background renders (each is minutes of CPU).
  const idempotencyKey = generateIdempotencyKey();
  if (onProgress) onProgress({ stage: "queued", progress_pct: 2, label });
  // v77.99.115p — Add the CSRF double-submit header so cookie-authed
  // requests bypass the CSRF middleware. The rest of the app relies
  // on the axios interceptor (lib/api.js) to attach this; the court
  // bundle helper uses raw `fetch()` (so we can stream the blob and
  // attach progress callbacks) and was the lone holdout. Production
  // reported HTTP 403 "Failed to start export (403)" from this
  // endpoint with an empty body — symptoms matching the
  // CsrfMiddleware path when the legacy `Authorization: Bearer ` (no
  // token) exempt path fails to fire reliably behind the CDN.
  const csrfMatch = typeof document !== "undefined"
    ? document.cookie.match(/(?:^|; )cpc_csrf=([^;]+)/)
    : null;
  const csrfToken = csrfMatch ? decodeURIComponent(csrfMatch[1]) : "";
  // v77.99.115q — `batch_id`, `batch_label`, `notify_email_on_ready`
  // moved from HEADERS into the JSON BODY. HTTP headers must be
  // ASCII (RFC 7230) but child names commonly contain accented
  // characters (Irish fada, French accents, Polish diacritics …);
  // Cloudflare and other CDNs silently 403 non-ASCII header values
  // BEFORE the request reaches our FastAPI process, which is why
  // production users saw "Failed to start export (403)." with no
  // body. The backend accepts both shapes during rollout.
  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
    "Idempotency-Key": idempotencyKey,
  };
  if (csrfToken) headers["X-CSRF-Token"] = csrfToken;
  // v77.99.152 (iter 130.cb-resilient) — Wrap the start fetch too.
  // A raw "Failed to fetch" TypeError bubbling out of here previously
  // showed the user only "Failed to fetch" with no context — turn
  // that into an actionable message.
  //
  // v77.99.163 (iter 141.cb-retry-always) — Retry the initial POST
  // up to 3 times with short backoff (1s, 2s) to ride through
  // transient deploy-window blips. A deploy can briefly black out
  // the backend for ~5-15s on this platform; without this, the
  // user's first courthouse-grade export of the day fails with
  // "Failed to fetch" and they have to think about whether to
  // retry themselves. The idempotency key means a successful but
  // slow first attempt is still dedup'd server-side.
  let startResp;
  let _start_attempt = 0;
  while (true) {
    _start_attempt += 1;
    try {
      startResp = await fetch(`${API}/exports/court-bundle/start`, {
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify({
          start_date,
          end_date,
          child_ids,
          sections,
          batch_id: batch_id || null,
          batch_label: label || null,
          batch_expected_count: batch_expected_count || 1,
          notify_email_on_ready: !!notify_email_on_ready,
          contact_visits_options: contact_visits_options || undefined,
          relationship_id: relationship_id || undefined,
        }),
      });
      // If the response is a transient 502/503/504, retry up to 3x.
      if ([502, 503, 504].includes(startResp.status) && _start_attempt < 3) {
        await sleep(_start_attempt * 1000);
        continue;
      }
      break;
    } catch (netErr) {
      if (_start_attempt < 3) {
        // Transient blip (likely deploy window) — brief backoff
        // and retry. Idempotency-Key ensures the server dedup's any
        // in-flight job on the second attempt.
        if (onProgress) onProgress({
          stage: "queued",
          progress_pct: 2,
          label: `Retrying export start (${_start_attempt}/3)`,
        });
        await sleep(_start_attempt * 1000);
        continue;
      }
      throw new Error(
        `Could not reach the export service (${netErr?.message || "network error"}). ` +
        `Check your connection and try again — your selections are preserved above.`,
      );
    }
  }
  if (!startResp.ok) {
    const j = await startResp.json().catch(() => ({}));
    // v77.99.115p — Backend HTTPExceptions sometimes pass a STRUCTURED
    // detail (e.g. `{error, message}` from the child-scope check or
    // `{error, plan, feature, message}` from the upgrade gate). The
    // previous `Error(j.detail)` stringified those to `[object
    // Object]`, hiding the actionable text. Prefer `detail.message`,
    // then a JSON dump, then the HTTP-code fallback so we never lose
    // signal again.
    let msg;
    if (typeof j.detail === "string" && j.detail) {
      msg = j.detail;
    } else if (j.detail && typeof j.detail === "object") {
      msg = j.detail.message || JSON.stringify(j.detail);
    } else {
      msg = `Failed to start export (${startResp.status}).`;
    }
    throw new Error(msg);
  }
  const { job_id } = await startResp.json();
  if (!job_id) throw new Error("Export server returned no job id.");
  // v77.99.157 (iter 135.cb-stall) — Expose the job_id to callers
  // via the progress callback the moment we have it, so the failure
  // card can offer a "Retry this exact bundle" action pointing at
  // `POST /exports/jobs/{id}/retry` if the server later flips it to
  // failed. Everything downstream is untouched — this is additive.
  if (onProgress) onProgress({ stage: "queued", progress_pct: 4, label, job_id });
  // 2. Poll until ready / failed. There is deliberately no wall-clock
  // timeout — the server owns stall detection. A very defensive
  // absolute ceiling of 6h is kept only so a dead-server browser tab
  // eventually stops polling.
  //
  // v77.99.152 (iter 130.cb-resilient) — Wrap each fetch in try/catch
  // so a single blip from the proxy (Cloudflare 5xx, TCP drop, DNS
  // hiccup) doesn't abort the export. Users were seeing a raw
  // "Failed to fetch" toast for what was really a transient poll
  // failure — the job on the server was still building successfully.
  const deadline = Date.now() + POLL_ABSOLUTE_CEILING_MS;
  let lastJob = null;
  let transientFailures = 0;
  const MAX_TRANSIENT = 20; // ~30s of pure-network failures before giving up
  while (Date.now() < deadline) {
    await sleep(POLL_INTERVAL_MS);
    let statusResp;
    try {
      statusResp = await fetch(`${API}/exports/jobs/${job_id}`, {
        credentials: "include",
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (netErr) {
      transientFailures += 1;
      if (transientFailures >= MAX_TRANSIENT) {
        throw new Error(
          `Network keeps dropping (${netErr?.message || "unknown"}). ` +
          `The bundle may still finish — check History → Downloads in a minute.`
        );
      }
      continue;
    }
    if (!statusResp.ok) {
      // 404 = job purged or unknown. Anything else = transient — retry.
      if (statusResp.status === 404) {
        throw new Error("Your export expired before download. Please try again.");
      }
      transientFailures += 1;
      if (transientFailures >= MAX_TRANSIENT) {
        throw new Error(`Status check kept failing (HTTP ${statusResp.status}).`);
      }
      continue;
    }
    transientFailures = 0;
    lastJob = await statusResp.json();
    // v77.99.29 — Surface stage + pct so the parent component can
    // render a progress card. We pass whatever the server gave us;
    // unknown stages fall back to "running" copy in the UI. v77.99.157
    // — also passes job_id so the caller has a stable retry handle.
    if (onProgress) {
      onProgress({
        stage: lastJob.progress_stage || lastJob.status || "running",
        progress_pct: lastJob.progress_pct ?? 10,
        label,
        size_bytes: lastJob.size_bytes,
        job_id,
      });
    }
    if (lastJob.status === "ready" || lastJob.status === "incomplete_attention_required") break;
    if (lastJob.status === "failed") {
      // Attach the job_id to the error so the failure card can offer
      // "Retry this bundle" without a fresh scope pick.
      const err = new Error(lastJob.error || "We couldn't complete this Court Bundle. Your records and selections are safe. You can retry this export.");
      err.job_id = job_id;
      err.failed_job = lastJob;
      throw err;
    }
  }
  if (!lastJob || (lastJob.status !== "ready" && lastJob.status !== "incomplete_attention_required")) {
    const err = new Error(
      "We couldn't complete this Court Bundle. Your records and selections are safe. You can retry this export."
    );
    err.job_id = job_id;
    throw err;
  }
  // 3. Download the cached bytes — fast single burst.
  if (onProgress) onProgress({ stage: "ready", progress_pct: 95, label });
  let dlResp;
  try {
    dlResp = await fetch(`${API}/exports/jobs/${job_id}/download`, {
      credentials: "include",
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch (netErr) {
    throw new Error(
      `Download connection dropped (${netErr?.message || "network error"}). ` +
      `The bundle is ready — open History → Downloads to fetch it.`,
    );
  }
  if (!dlResp.ok) {
    const j = await dlResp.json().catch(() => ({}));
    throw new Error(j.detail || `Download failed (${dlResp.status}).`);
  }
  const blob = await dlResp.blob();
  const fingerprint = lastJob.fingerprint || dlResp.headers.get("X-Bundle-Fingerprint") || "bundle";
  // Compose a friendly local filename: server's name is fine but we
  // already have richer label context here (e.g. child name).
  const filename = lastJob.filename || `court-bundle-${label}-${start_date}-to-${end_date}-${fingerprint}.pdf`;
  // Trigger browser save dialog.
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  if (onProgress) onProgress({ stage: "ready", progress_pct: 100, label, size_bytes: blob.size });
  return { blob, fingerprint, filename };
}


/**
 * v77.99.157 (iter 135.cb-stall) — Retry a failed court bundle job
 * with its ORIGINAL frozen snapshot (same range, children, sections,
 * relationship, contact_visits_options). The server persisted the
 * request body on the job doc at enqueue time, so the retry endpoint
 * can rehydrate it without any client-side reconstruction.
 *
 * The original failed job is preserved in History (not overwritten);
 * a new job_id is minted and cross-linked with `retry_of` / `retried_as`
 * so the audit trail shows both attempts.
 *
 * Returns `{ blob, fingerprint, filename }` on success — identical to
 * `runAsyncCourtBundleJob`. Throws a `toast`-ready Error on failure.
 */
export async function retryCourtBundleJob({ failed_job_id, label, onProgress }) {
  const token = getToken();
  const csrfMatch = typeof document !== "undefined"
    ? document.cookie.match(/(?:^|; )cpc_csrf=([^;]+)/)
    : null;
  const csrfToken = csrfMatch ? decodeURIComponent(csrfMatch[1]) : "";
  const headers = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
  if (csrfToken) headers["X-CSRF-Token"] = csrfToken;
  let retryResp;
  try {
    retryResp = await fetch(`${API}/exports/jobs/${failed_job_id}/retry`, {
      method: "POST",
      headers,
      credentials: "include",
    });
  } catch (netErr) {
    throw new Error(
      `Could not reach the export service (${netErr?.message || "network error"}). ` +
      `Your records and selections are safe — please try again in a moment.`,
    );
  }
  if (!retryResp.ok) {
    const j = await retryResp.json().catch(() => ({}));
    let msg;
    if (typeof j.detail === "string" && j.detail) {
      msg = j.detail;
    } else if (j.detail && typeof j.detail === "object") {
      msg = j.detail.message || JSON.stringify(j.detail);
    } else {
      msg = `Retry failed (${retryResp.status}).`;
    }
    throw new Error(msg);
  }
  const { job_id } = await retryResp.json();
  if (!job_id) throw new Error("Retry service returned no job id.");
  if (onProgress) onProgress({ stage: "queued", progress_pct: 4, label, job_id });
  // Poll + download using the same shared logic as the primary
  // export path. Extract into a private helper to keep both paths
  // identical.
  return _pollAndDownload({ job_id, label, onProgress, token });
}


/**
 * Poll a queued/running export job to completion and trigger the
 * browser download. Shared between `runAsyncCourtBundleJob` (freshly
 * enqueued jobs) and `retryCourtBundleJob` (re-runs of a failed
 * bundle's frozen snapshot).
 */
async function _pollAndDownload({ job_id, label, onProgress, token }) {
  const deadline = Date.now() + POLL_ABSOLUTE_CEILING_MS;
  let lastJob = null;
  let transientFailures = 0;
  const MAX_TRANSIENT = 20;
  while (Date.now() < deadline) {
    await sleep(POLL_INTERVAL_MS);
    let statusResp;
    try {
      statusResp = await fetch(`${API}/exports/jobs/${job_id}`, {
        credentials: "include",
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (netErr) {
      transientFailures += 1;
      if (transientFailures >= MAX_TRANSIENT) {
        throw new Error(
          `Network keeps dropping (${netErr?.message || "unknown"}). ` +
          `The bundle may still finish — check History → Downloads in a minute.`
        );
      }
      continue;
    }
    if (!statusResp.ok) {
      if (statusResp.status === 404) {
        throw new Error("Your export expired before download. Please try again.");
      }
      transientFailures += 1;
      if (transientFailures >= MAX_TRANSIENT) {
        throw new Error(`Status check kept failing (HTTP ${statusResp.status}).`);
      }
      continue;
    }
    transientFailures = 0;
    lastJob = await statusResp.json();
    if (onProgress) {
      onProgress({
        stage: lastJob.progress_stage || lastJob.status || "running",
        progress_pct: lastJob.progress_pct ?? 10,
        label,
        size_bytes: lastJob.size_bytes,
        job_id,
      });
    }
    if (lastJob.status === "ready" || lastJob.status === "incomplete_attention_required") break;
    if (lastJob.status === "failed") {
      const err = new Error(lastJob.error || "We couldn't complete this Court Bundle. Your records and selections are safe. You can retry this export.");
      err.job_id = job_id;
      err.failed_job = lastJob;
      throw err;
    }
  }
  if (!lastJob || (lastJob.status !== "ready" && lastJob.status !== "incomplete_attention_required")) {
    const err = new Error(
      "We couldn't complete this Court Bundle. Your records and selections are safe. You can retry this export."
    );
    err.job_id = job_id;
    throw err;
  }
  if (onProgress) onProgress({ stage: "ready", progress_pct: 95, label });
  let dlResp;
  try {
    dlResp = await fetch(`${API}/exports/jobs/${job_id}/download`, {
      credentials: "include",
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch (netErr) {
    throw new Error(
      `Download connection dropped (${netErr?.message || "network error"}). ` +
      `The bundle is ready — open History → Downloads to fetch it.`,
    );
  }
  if (!dlResp.ok) {
    const j = await dlResp.json().catch(() => ({}));
    throw new Error(j.detail || `Download failed (${dlResp.status}).`);
  }
  const blob = await dlResp.blob();
  const fingerprint = lastJob.fingerprint || dlResp.headers.get("X-Bundle-Fingerprint") || "bundle";
  const filename = lastJob.filename || `court-bundle-${label || "retry"}-${fingerprint}.pdf`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  if (onProgress) onProgress({ stage: "ready", progress_pct: 100, label, size_bytes: blob.size });
  return { blob, fingerprint, filename };
}
